// Deprecated - This file should not be added to anymore.
// please use an "api-*.ts" file for communicating with the API
import axios from "axios";

import { PatientCache } from "./cacheUtils";
import { generatePatientSummaries } from "./api-documentation-patient-summary";

const isStaging = () => {
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href.toLowerCase();
    return (
      currentUrl.includes("localhost") ||
      currentUrl.includes("staging") ||
      currentUrl.includes("dev.")
    );
  }
};

const CLINICOS_API_BASE_URL = isStaging()
  ? "https://api-staging.clinicos.ai"
  : "https://api.clinicos.ai";

// Initialize patient cache
const patientCache = new PatientCache();
patientCache.loadFromStorage();

/**
 * Fetch date range schedule from ClinicOS API - fetches appointments for multiple days
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} clinicId - Required clinic ID for ClinicOS integration
 * @param {Object} session - User session with access token
 * @param {boolean} useDummyData - Flag to request dummy data (default: false)
 * @returns {Array} Array of objects with date and appointments
 */
export const fetchDateRangeScheduleClinicOS = async (
  startDate,
  endDate,
  clinicId,
  session,
  useDummyData,
) => {
  if (!clinicId) {
    console.error("Clinic ID is required for ClinicOS API");
    throw new Error("Clinic ID is required for ClinicOS API");
  }

  try {
    // Temporary client-side workaround:
    // The appointments endpoint currently interprets date-only end_date as
    // midnight, so a same-day range can exclude daytime appointments.
    // For daily windows (start === end), send end_date as next day.
    const normalizedEndDate =
      startDate && endDate && startDate === endDate
        ? (() => {
            const [year, month, day] = endDate.split("-").map(Number);
            const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
            return nextDay.toISOString().split("T")[0];
          })()
        : endDate;

    // Call ClinicOS API
    const response = await axios.get(
      `${CLINICOS_API_BASE_URL}/clinics/v1/appointment/${clinicId}/appointments`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${session.accessToken}`,
        },
        params: {
          start_date: startDate,
          end_date: normalizedEndDate,
          use_dummy_data: useDummyData,
          include_patient_summary: true,
        },
      },
    );

    if (!response.data || !response.data.appointments) {
      return [];
    }

    // Transform appointments (similar to fetchDailyScheduleClinicOS)
    const processedAppointments = response.data.appointments.map(
      (appointment) => {
        // Extract date from the appointment
        const appointmentDate = appointment.date
          ? new Date(appointment.date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0];

        // Format time
        const dateTime = appointment.date;
        // Get user's timezone
        // Convert the ISO string to local time directly
        const options = {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        };
        const formattedTime = new Date(dateTime).toLocaleTimeString(
          "en-US",
          options,
        );

        // Log for debugging
        // Extract procedure types
        const abbreviatedProcedures = appointment.formatted_procedures || [];
        const patientUuidRaw = appointment.patient_uuid;
        const isIntPatientUuid =
          (typeof patientUuidRaw === "number" &&
            Number.isInteger(patientUuidRaw)) ||
          (typeof patientUuidRaw === "string" && /^\d+$/.test(patientUuidRaw));
        if (isIntPatientUuid) {
          console.warn("PATIENT UUID IS INT", {
            appointmentId: appointment.id,
            patient_uuid: patientUuidRaw,
            clinicId,
            startDate,
            endDate,
          });
        }

        return {
          id:
            appointment.id ||
            `clinicos-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name: "Unknown Patient", // Will be updated with patient details
          firstName: "",
          lastName: "",
          appointmentTime: formattedTime,
          procedureType: appointment.formatted_procedures[0] || "TUMMY_TUCK",
          // procedureType:  "TUMMY_TUCK",
          appointmentId: appointment.id,

          consultationDate: appointmentDate,
          hasPhotos: false,
          hasSimulations: false,
          metadata: {
            age: 30,
            bodyType: "Average",
            concernSeverity: "Moderate",
            desiredOutcome: appointment.reason || "Natural enhancement",
          },
          abbreviatedProcedures,
          originalPhotos: [],
          email: "",
          phone: "",
          patientRecordId: appointment.patient_uuid,
          visitType: appointment.type || "Consultation",
          clinicOSUUID: appointment.patient_uuid,
          rawFields: appointment,
          isDummyData: useDummyData, // Add flag to indicate data type
          patientSummary: appointment.patient_summary || "",
        };
      },
    );

    // Phase 2: Fetch patient details to enrich appointments
    const patientUuids = processedAppointments
      .map((appointment) => appointment.patientRecordId)
      .filter(Boolean);

    // Generate patient summaries for appointments without summaries
    try {
      // Find appointments that don't have patient summaries and aren't dummy data
      const appointmentsNeedingSummaries = processedAppointments.filter(
        (appointment) =>
          !appointment.isDummyData && // Skip dummy data
          !appointment.patientSummary && // No existing summary
          appointment.patientRecordId && // Has valid patient UUID
          appointment.appointmentId, // Has valid appointment ID
      );

      if (appointmentsNeedingSummaries.length > 0) {
        const patientsForSummary = appointmentsNeedingSummaries.map(
          (appointment) => ({
            patient_uuid: appointment.patientRecordId,
            appointment_id: appointment.appointmentId,
          }),
        );

        // Fire and forget - generate summaries in background
        generatePatientSummaries(session, clinicId, {
          patients_info: patientsForSummary,
          update_existing_summaries: false,
        }).catch((error) => {
          console.warn("Failed to trigger patient summary generation:", error);
        });

        console.log(
          `Triggered summary generation for ${appointmentsNeedingSummaries.length} appointments`,
        );
      }
    } catch (error) {
      console.warn("Error in patient summary generation trigger:", error);
    }

    if (patientUuids.length > 0) {
      try {
        const uniquePatientUuids = [...new Set(patientUuids)];

        // Fetch all patient details in a single API call
        const patientDetails = await fetchPatientsFromClinicOS(
          clinicId,
          { patient_ids: uniquePatientUuids, use_dummy_data: useDummyData }, // Use dummy data flag
          session,
        );

        // console.log(
        //   `Retrieved ${patientDetails.length} patients from ClinicOS`,
        // );

        // Create a lookup map for quick access
        const patientMap = {};
        patientDetails.forEach((patient) => {
          patientMap[patient.uuid] = patient;
        });

        // Enrich appointments with patient details
        processedAppointments.forEach((appointment) => {
          const patientUuid = appointment.patientRecordId;
          const patientDetail = patientMap[patientUuid];

          if (patientDetail) {
            appointment.name =
              `${patientDetail.first_name || ""} ${patientDetail.last_name || ""}`.trim();
            appointment.firstName = patientDetail.first_name || "";
            appointment.lastName = patientDetail.last_name || "";
            appointment.email = patientDetail.email || "";
            appointment.phone = patientDetail.phone || "";

            // Enrich with additional details if available
            if (patientDetail.date_of_birth) {
              appointment.metadata.age = calculateAge(
                patientDetail.date_of_birth,
              );
            }
          }
        });
      } catch (error) {
        console.error("Error fetching patient details:", error);
        // Continue with appointments without patient enrichment
      }
    }

    // Group by date
    const dateMap = {};
    processedAppointments.forEach((appointment) => {
      const date = appointment.consultationDate;
      if (!dateMap[date]) {
        dateMap[date] = [];
      }
      dateMap[date].push(appointment);
    });

    // Generate all dates in the range (to include days with no appointments)
    const allDates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      const dateStr = currentDate.toISOString().split("T")[0];
      if (!dateMap[dateStr]) {
        dateMap[dateStr] = []; // Ensure empty array for dates with no appointments
      }
      allDates.push(dateStr);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Convert to array format expected by the UI
    const result = allDates.map((date) => ({
      date,
      data: dateMap[date] || [],
    }));

    return result;
  } catch (error) {
    console.error("Error fetching date range schedule from ClinicOS:", error);
    throw error;
  }
};

/**
 * Fetch patient details from ClinicOS API
 * @param {string} clinicId - Required clinic ID
 * @param {Object} filters - Optional filters (patient_ids, email, phone, search, name, etc.)
 * @returns {Array} Array of patient objects
 */
const fetchPatientsFromClinicOS = async (clinicId, filters = {}, session) => {
  if (!clinicId) {
    console.error("Clinic ID is required for ClinicOS API");
    throw new Error("Clinic ID is required for ClinicOS API");
  }

  if (!session?.accessToken) {
    console.error("Valid session with access token is required");
    throw new Error("Missing authentication: session.accessToken");
  }

  try {
    const baseUrl = `${CLINICOS_API_BASE_URL}/clinics/v1/clinic/${clinicId}/patients`;

    // Manually build the complete query string
    const queryParts = [];

    // Handle patient_ids - manually build the correct format
    if (filters.patient_ids && filters.patient_ids.length > 0) {
      const patientIdsQuery = filters.patient_ids
        .map((id) => `patient_ids=${encodeURIComponent(id)}`)
        .join("&");
      queryParts.push(patientIdsQuery);

      console.log(
        `🔍 Adding ${filters.patient_ids.length} patient IDs to query`,
      );
      console.log(
        `🔗 Sample patient_ids query: ${patientIdsQuery.substring(0, 100)}...`,
      );
    }

    // Handle name search
    if (filters.search && filters.search.trim() !== "") {
      queryParts.push(`search=${encodeURIComponent(filters.search.trim())}`);
    }

    if (filters.name && filters.name.trim() !== "") {
      queryParts.push(`name=${encodeURIComponent(filters.name.trim())}`);
      console.log(`🔍 Adding name filter: "${filters.name.trim()}"`);
    }

    // Handle substring search
    if (filters.use_substring !== undefined) {
      queryParts.push(
        `use_substring=${encodeURIComponent(filters.use_substring)}`,
      );
      console.log(`🔍 Adding substring search: ${filters.use_substring}`);
    }

    if (filters.use_dummy_data !== undefined) {
      queryParts.push(
        `use_dummy_data=${encodeURIComponent(filters.use_dummy_data)}`,
      );
      console.log(`🧪 Adding use_dummy_data flag: ${filters.use_dummy_data}`);
    }

    if (filters.limit !== undefined) {
      queryParts.push(`limit=${encodeURIComponent(filters.limit)}`);
    }

    if (filters.cursor !== undefined) {
      queryParts.push(`cursor=${encodeURIComponent(filters.cursor)}`);
    }

    // Build the complete URL with query string
    const fullUrl =
      queryParts.length > 0 ? `${baseUrl}?${queryParts.join("&")}` : baseUrl;

    // console.log(
    //   "🌐 Complete request URL:",
    //   fullUrl.substring(0, 150) + (fullUrl.length > 150 ? "..." : ""),
    // );
    // console.log("🔍 ClinicOS API call details:", {
    //   baseUrl,
    //   queryPartsCount: queryParts.length,
    //   hasPatientIds: !!filters.patient_ids?.length,
    //   patientIdsCount: filters.patient_ids?.length || 0,
    //   hasNameFilter: !!filters.name?.trim(),
    //   originalFilters: {
    //     patient_ids_count: filters.patient_ids?.length || 0,
    //     name: filters.name || null,
    //     use_substring: filters.use_substring,
    //   },
    // });

    // Make the request using the manually built URL (no params needed)
    const response = await axios.get(fullUrl, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${session.accessToken}`,
      },
      // Note: No params here since we built the query string manually
    });

    const patientCount = response.data?.patients?.length || 0;

    // Enhanced result logging
    if (filters.patient_ids?.length > 0) {
      const expectedCount = filters.patient_ids.length;
      const ratio = patientCount / expectedCount;
    } else {
      console.log(
        `🔍 No patient filter applied - returned ${patientCount} patients`,
      );
    }

    if (!response.data || !response.data.patients) {
      console.log("📭 No patients data in response");
      return [];
    }

    return response.data.patients;
  } catch (error) {
    throw error;
    console.error("❌ Error fetching patients from ClinicOS:", error);
  }
};

/**
 * Search for patients in the ClinicOS API
 * @param {string} searchTerm - The raw patient search text
 * @param {string} clinicId - Required clinic ID for ClinicOS integration
 * @returns {Array} Array of matched patient records
 */
export const searchPatientsClinicOS = async (
  searchTerm,
  clinicId,
  session,
  useDummyData = false,
) => {
  if (!clinicId) {
    console.error("Clinic ID is required for ClinicOS API");
    throw new Error("Clinic ID is required for ClinicOS API");
  }

  try {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    // Fetch patients from ClinicOS
    const patients = await fetchPatientsFromClinicOS(
      clinicId,
      {
        search: searchTerm,
        limit: 20,
        use_dummy_data: useDummyData, // Pass dummy data flag if needed
      },
      session,
    );

    // Transform the data to match our UI expectations
    const processedPatients = patients.map((patient) => ({
      id: patient.id || patient.uuid,
      name:
        `${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
        "Unknown Patient",
      firstName: patient.first_name || "",
      lastName: patient.last_name || "",
      appointmentTime: "",
      procedureType: "", // Default
      consultationDate: new Date().toISOString().split("T")[0], // Current date as placeholder
      hasPhotos: false, // Default
      hasSimulations: false, // Default
      metadata: {
        age: patient.date_of_birth ? calculateAge(patient.date_of_birth) : 30,
        bodyType: "Average", // Default
        concernSeverity: "Moderate", // Default
        desiredOutcome: "Natural enhancement", // Default
      },
      abbreviatedProcedures: [], // Default
      email: patient.email || "",
      phone: patient.phone || "",
      patientRecordId: patient.uuid,
      clinicOSUUID: patient.uuid,
      rawFields: patient, // Store original data for reference
    }));

    return processedPatients;
  } catch (error) {
    console.error("Error searching patients by name in ClinicOS:", error);
    return [];
  }
};

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - Date of birth in ISO format
 * @returns {number} Age in years
 */
function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return 30; // Default age

  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

/**
 * Helper to determine view type from image metadata
 */
function determineViewFromMetadata(image) {
  // Try to extract view from metadata
  if (image.image_data.view_type_human_readable) {
    return image.image_data.view_type_human_readable;
  } else if (image.image_data.view_type) {
    return image.image_data.view_type;
  } else if (image.image_data.upload_type === "SideBySideImage") {
    return "Side by Side";
  } else if (image.image_data.upload_type === "BeforeAfterImageMetadata") {
    return "Before & After";
  }

  // Default view type
  return "Front";
}

/**
 * Helper to determine procedure type from image metadata
 */
function determineProcedureFromMetadata(image) {
  // Try to extract procedure from metadata
  if (
    image.image_data?.procedure_types_human_readable &&
    Array.isArray(image.image_data.procedure_types_human_readable) &&
    image.image_data.procedure_types_human_readable.length > 0
  ) {
    return image.image_data.procedure_types_human_readable[0];
  } else if (
    image.image_data?.procedure_types &&
    Array.isArray(image.image_data.procedure_types) &&
    image.image_data.procedure_types.length > 0
  ) {
    return image.image_data.procedure_types[0];
  }

  // Default procedure type
  return "";
}

export default {
  patientCache,
  fetchDateRangeScheduleClinicOS,
  searchPatientsClinicOS,
};
